import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { RootState } from "../../state/store";
import { setCurrentView } from "../../state/reducers/viewReducer";
import { DateSelectCrumb } from "../../components/Breadcrumbs/DateSelectCrumb";
import { ButtonCrumb } from "../../components/Breadcrumbs/ButtonCrumb";
import OperationsTable from "./OperationsTable";
import TablePage from "../../components/Layout/TablePage";
import SaveButton from "../../components/Buttons/SaveButton";
import { useGlobalModal } from "../../components/Contexts/GlobalModalContext";
import MultipleStoppagesModal from "./MultipleStoppagesModal";
import { OptionResponse } from "../../DTOs/Common/OptionResponse";
import { stoppagesAjax } from "../../ajax/StoppagesAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { useSnackbar } from "notistack";
import { operationsAjax } from "../../ajax/OperationsAjax";
import { setSelectedDate, setSelectedShift } from "../../state/reducers/shiftReducer";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import { referencesAjax } from "../../ajax/ReferencesAjax";
import { useOptions } from "../../components/Hooks/useOptions";
import { TripleShift } from "../../types/Shift";
import { ShiftSelectCrumb } from "../../components/Breadcrumbs/Shift/ShiftSelectCrumb";
import { StoppageCalc } from "../../DTOs/Stoppage/StoppageCalc";
import { PagedResult } from "../../DTOs/Common/PagedResult";
import { enqueue } from "../../offline/queueOps";
import { computeShiftId } from "../../offline/computeShiftId";

const Operations: React.FC = () => {
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const isShiftLocked = useSelector((state: RootState) => state.shift.isLocked);
  const latestShiftDate = useSelector((state: RootState) => state.shift.latestShiftDate);
  const latestShift = useSelector((state: RootState) => state.shift.latestShift);
  const { selectedDate, selectedShift } = useShiftTimes();
  const queryClient = useQueryClient();
  const [localSelectedShift, _setLocalSelectedShift] = useState<TripleShift>(selectedShift)
  const dispatch = useDispatch();
  const { showModal, hideModal } = useGlobalModal();
  const { enqueueSnackbar } = useSnackbar();
  const { data: stoppageTypes = new OptionResponse([]) } = useQuery({
    queryKey: ['references', 'stoppageTypes'],
    queryFn: async () => {
      const res = await stoppagesAjax.getStoppageTypes();
      if (ErrorResponse.isErrorResponse(res)) return new OptionResponse([]);
      return res;
    },
    staleTime: 5 * 60 * 1000,
  });
  const [refreshFn, setRefreshFn] = useState<() => void>(() => () => { });
  const {
    options: locationOptions,
    isLoading: isLocationOptionsLoading
  } = useOptions<LocationDTO, LocationDTO>(
    ['references', 'locations'],
    () => referencesAjax.getLocations({}),
    x => x,
  )

  const triggerRefresh = useCallback(() => {
    refreshFn?.();
  }, [refreshFn]);

  const setLocalSelectedShift = useCallback((newShift: TripleShift) => {
    _setLocalSelectedShift(newShift);

    if (newShift === "DAY" || newShift === "NIGHT") {
      dispatch(setSelectedShift(newShift));
    }
  }, [dispatch]);

  const showMultipleStoppagesModal = async () => {
    showModal({
      content: () => (<MultipleStoppagesModal
        stoppageTypeGroupOptions={stoppageTypeGroupOptions}
        selectedDate={selectedDate}
        selectedShift={localSelectedShift}
        closeModal={hideModal}
        refreshPage={triggerRefresh}
      />
      ),
      tailwindMaxWidthClass: 'max-w-2xl'
    });
    return true;
  };

  useEffect(() => {
    const newView = "Operations";

    if (currentView !== newView) {
      dispatch(setCurrentView(newView));
    }
  }, [currentView, dispatch]);

  const stoppageTypeGroupOptions = useMemo(() => {
    return stoppageTypes.groups.flatMap(group => group.options.map(o => ({
      value: o.key,
      label: o.label,
      groupColor: group.fullObject.colourHex,
      stoppageType: group.label,
      downtimeTypeId: group.key
    })));
  }, [stoppageTypes.groups]);

  const buildProvisionalOps = useCallback(() => {
    const nextDateStr = selectedDate.plus({ days: 1 }).toISODate()!
    const currentQueries = queryClient.getQueriesData<PagedResult<StoppageCalc>>({
      queryKey: ['operations', 'stoppageCalcs', selectedDate.toISODate()!, localSelectedShift]
    })
    for (const [currentKey, data] of currentQueries) {
      const plants = data?.data ?? []
      const makeProvisional = (shift: TripleShift): PagedResult<StoppageCalc> => {
        const shiftId = computeShiftId(nextDateStr, shift === 'DAY' ? 'DAY' : 'NIGHT')
        const ops = plants.map((p, idx) => new StoppageCalc({
          operationId: -(Date.now() + idx),
          shiftId,
          plantId: p.plantId,
          machineName: p.machineName,
          status: 'UP',
          startEngineHours: p.endEngineHours ?? 0,
          locationId: p.locationId,
        }))
        return new PagedResult(ops, ops.length, Math.max(1, ops.length), 1, 1000)
      }
      const keyArr = currentKey as unknown[]
      queryClient.setQueryData([...keyArr.slice(0, 2), nextDateStr, 'DAY', ...keyArr.slice(4)], makeProvisional('DAY'))
      queryClient.setQueryData([...keyArr.slice(0, 2), nextDateStr, 'NIGHT', ...keyArr.slice(4)], makeProvisional('NIGHT'))
    }
  }, [queryClient, selectedDate, localSelectedShift])

  const completeShift = async () => {
    if (!navigator.onLine) {
      const currentShiftId = computeShiftId(selectedDate.toISODate()!, localSelectedShift)
      // Note: build provisional next-shift ops only when completing the system's latest shift.
      // "Latest" is the persisted latest tblShift record, never DateTime.now(). Two documented
      // limitations (see EMERGENCY_RUNBOOK.md): the stored latest is not advanced on completion,
      // so back-to-back offline completions only seed the first; and AFTERNOON never matches
      // because the latest shift is stored as DAY/NIGHT only.
      const isLatestShift = latestShiftDate === selectedDate.toISODate() && latestShift === localSelectedShift
      if (isLatestShift) {
        buildProvisionalOps()
      }
      await enqueue({ entity: 'completeShift', operation: 'create', shiftId: currentShiftId })
      showModal({
        content: ({ close }) => (
          <div className="flex flex-col gap-4 p-2">
            <p className="text-theme-text text-sm">
              {isLatestShift
                ? 'No network connection. A provisional shift has been created from cached data so you can continue entering data for the next shift immediately. The shift will be completed fully when connectivity is restored: engine hours will be calculated, drilling patterns carried over, and validation run.'
                : 'No network connection. The shift completion will be applied when connectivity is restored.'
              }
            </p>
            <button onClick={close} className="self-end eclipse-btn-primary px-4 py-1.5 text-sm">
              OK
            </button>
          </div>
        ),
        tailwindMaxWidthClass: 'max-w-md',
      })
      return true;
    }
    const res = await operationsAjax.completeShift(selectedDate, localSelectedShift);
    if (!res.errors && !res.data) {
      enqueueSnackbar("Shift completed successfully.", { variant: 'success', autoHideDuration: 3000 });
      return true;
    }
    res.errors?.forEach((error, index) => {
      enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 });
    });
    return false;
  };

  return (
    <TablePage>
      <Breadcrumbs>
        <DateSelectCrumb
          selectedDate={selectedDate}
          setSelectedDate={(date) => dispatch(setSelectedDate(date.toISODate()!))}
        />
        <ShiftSelectCrumb
          showAfternoonShift
          selectedShift={localSelectedShift}
          setSelectedShift={setLocalSelectedShift}
        />
        <ButtonCrumb buttonText="Operations List" />
      </Breadcrumbs>
      <div className="flex flex-row gap-1 items-center justify-center shrink-0">
        <SaveButton
          buttonLabel={"Multiple Stoppage"}
          requireConfirmation={false}
          handleSave={showMultipleStoppagesModal}
          disabled={isShiftLocked}
          styleClasses="short:py-1"
        />
        <SaveButton
          buttonLabel={"Complete Shift"}
          requireConfirmation={false}
          handleSave={completeShift}
          disabled={isShiftLocked}
          styleClasses="short:py-1"
        />
      </div>
      <OperationsTable
        selectedShift={localSelectedShift}
        selectedDate={selectedDate}
        stoppageTypeGroupOptions={stoppageTypeGroupOptions}
        locationOptions={locationOptions}
        isLocationOptionsLoading={isLocationOptionsLoading}
        setRefreshFn={setRefreshFn}
      />
    </TablePage>
  );
};

export default Operations;